/**
 * 設定のフローティングダイアログ。
 *
 * ページを切り替えるのではなく、いまの画面（カタログ）の上に大きめのダイアログとして重ねる。
 * 枠と見出しで「設定を開いている」ことが一目で分かり、× / Esc / 外側クリックで元の画面に戻る。
 *
 * ネイティブ <dialog> の showModal() を使う:
 * - 背後の画面は自動で inert（タブ移動・クリックが届かない）
 * - フォーカスはダイアログ内に閉じ、閉じると開く前の要素（サイドバーの歯車）へ戻る
 *
 * **閉じる経路は自前で持つ**（×・外側クリック・Esc → onClose → 親が外す）。
 * 'close' イベントに頼らない — Chrome 148 系で close() しても 'close' が発火しないのを実測した
 * （'toggle' と 'cancel' は発火する）。ブラウザ側で閉じられた場合（戻る操作・requestClose）だけ
 * 'close' / 'cancel' を拾って状態を合わせる。取り外す直前に close() を呼ぶのは、
 * ネイティブのフォーカス復帰（開く前の要素へ戻す）を走らせるため。
 *
 * <dialog> 自体は透明な全画面の器にし、見た目のパネルを中に置く。
 * 器がクリックされた＝パネルの外側、と判定できる。
 */
import { useLayoutEffect, useRef, type ReactNode } from 'react'
import { GearIcon } from './Sidebar.tsx'

export function SettingsDialog({
  title = '設定',
  onClose,
  children,
}: {
  title?: string
  onClose: () => void
  children: ReactNode
}) {
  const ref = useRef<HTMLDialogElement | null>(null)
  const headingRef = useRef<HTMLHeadingElement | null>(null)
  // 親の再描画で onClose の参照が変わっても、開き直し・フォーカス奪取をしない
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  // useLayoutEffect: 取り外し時の cleanup が DOM から外れる**前**に走る（useEffect だと外れた後）。
  // 外れる前に close() しないと、ネイティブのフォーカス復帰が起きない。
  useLayoutEffect(() => {
    const dialog = ref.current
    if (!dialog) return
    if (!dialog.open) dialog.showModal()
    // 最初の操作要素（×）ではなく見出しへ。読み上げで「設定」と分かってから中身へ進める
    headingRef.current?.focus()
    const close = () => onCloseRef.current()
    const cancel = (e: Event) => {
      e.preventDefault()
      close()
    }
    dialog.addEventListener('close', close)
    dialog.addEventListener('cancel', cancel)
    return () => {
      dialog.removeEventListener('close', close)
      dialog.removeEventListener('cancel', cancel)
      if (dialog.open) dialog.close()
    }
  }, [])

  return (
    <dialog
      ref={ref}
      className="top-dialog settings-dialog"
      aria-labelledby="settings-dialog-title"
      onClick={(e) => {
        // 器そのものがクリックされた＝パネルの外側
        if (e.target === e.currentTarget) onClose()
      }}
      onKeyDown={(e) => {
        if (e.key !== 'Escape') return
        // IME 変換中の Esc は変換の取り消しであって、閉じる操作ではない
        if (e.nativeEvent.isComposing || e.keyCode === 229) return
        // ネイティブの cancel に任せず自前で閉じる（経路を1本にする）
        e.preventDefault()
        e.stopPropagation()
        onClose()
      }}
    >
      <div className="settings-dialog-panel">
        <header className="settings-dialog-head">
          <GearIcon />
          <h2 id="settings-dialog-title" ref={headingRef} tabIndex={-1}>
            {title}
          </h2>
          <span className="muted small settings-dialog-hint">Esc で閉じる</span>
          <button
            type="button"
            className="settings-dialog-close"
            aria-label="設定を閉じる"
            title="閉じる"
            onClick={onClose}
          >
            ×
          </button>
        </header>
        <div className="settings-dialog-body">{children}</div>
      </div>
    </dialog>
  )
}
